import { Concept, Model, ModelType, Workflow } from 'api/grpc/resources';
import Router from 'next/router';
import { getEnv, types as t } from 'mst-effect';
import { updateMST, withRunInAction } from 'utils/withRunInAction';
import { createValidConceptName } from 'utils/concepts';
import { pipe } from 'fp-ts/lib/function';
import { either } from 'fp-ts';
import { createConcept } from 'api-proxy/public/createConcept';

// The entity represented by the filter item.
export interface BaseEntityItem {
  getId: () => string;
  getName?: () => string;
}

export interface FilterItem {
  id: string;
  negate: boolean;
}

export interface ConceptFilter extends FilterItem {}

export enum FilterOperator {
  OR = 'or',
  AND = 'and',
}

/**
 * Store that manages storing concepts, fetching and selecting them
 */
export const ConceptFiltersStore = t
  .compose(
    withRunInAction(),
    t.model('ConceptFiltersStore', {
      /** All loaded concepts */
      trainedConcepts: t.array(t.frozen<Concept>()),
      untrainedConcepts: t.array(t.frozen<Concept>()),

      /** Currently selected concepts to filter */
      conceptFilters: t.array(
        t.model({
          id: t.string,
          negate: t.boolean,
        }),
      ),

      /** Indicates if all concepts have been fetched */
      allConceptsFetched: t.optional(t.boolean, false),

      /** Indicates if user has selected to show unlabeled inputs */
      shouldShowUnlabeled: t.optional(t.boolean, false),
      baseWorkflow: t.maybeNull(t.frozen<Workflow>()),
      conceptsFilterOperator: t.optional(t.enumeration('filterOperator', Object.values(FilterOperator)), FilterOperator.OR),
      rankableModel: t.maybeNull(t.frozen<Model>()),
    }),
  )
  .views((self) => {
    function _allConcepts() {
      return self.trainedConcepts.concat(self.untrainedConcepts).sort((a, b) => a.getName().localeCompare(b.getName()));
    }

    return {
      // gets overridden so technically lacks coverage
      // istanbul ignore next
      get env() {
        return getEnv<CF.MSTEnvironment>(self);
      },

      /**
       * All loaded concept IDs but only if shouldShowUnlabeled is set to true
       */
      get unlabeledFilter() {
        return self.shouldShowUnlabeled && self.allConceptsFetched ? _allConcepts().map((concept) => concept.getId()) : [];
      },

      get allConcepts() {
        return _allConcepts();
      },
    };
  })
  .actions((self) => ({
    clearConceptFilters() {
      self.runInAction(() => {
        self.conceptFilters.replace([]);
      });
    },

    getConceptFilter(conceptId: string) {
      return self.conceptFilters.find(({ id }) => id === conceptId);
    },

    /**
     * Add a concept filter
     */
    addConceptFilter: (filter: ConceptFilter) => {
      if (!self.shouldShowUnlabeled) {
        self.conceptFilters.push(filter);
      }
    },

    setConceptsFilterOperator: (op: FilterOperator) => {
      self.conceptsFilterOperator = op;
    },
    setBaseWorkflow(workflow: Workflow) {
      self.baseWorkflow = workflow;
    },
    setRankableModel(model: Model | null) {
      self.rankableModel = model;
    },
  }))
  .actions((self) => ({
    async getBaseWorkflow() {
      const { grpc } = self.env;
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      const resp = await grpc.getWorkflow({ userOrOrgId, appId });
      pipe(
        resp,
        either.match(
          (e) => {
            console.warn('Cannot load default workflow', e);
          },
          (workflowResponse) => {
            const workflow = workflowResponse.getWorkflow();
            if (workflow) self.setBaseWorkflow(workflow);
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    setShouldShowUnlabeled: (showUnlabeled: boolean) => {
      self.clearConceptFilters();
      self.shouldShowUnlabeled = showUnlabeled;
    },

    getUnlabeledFilter() {
      return self.unlabeledFilter;
    },

    /**
     * Fetch all concepts in the app
     */
    async fetchAllConcepts() {
      const { grpc } = self.env;
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      if (!self.baseWorkflow) {
        await self.getBaseWorkflow();
      }
      if (!userOrOrgId || !appId) {
        return Promise.reject(new Error('Missing user/org ID or App ID'));
      }

      const modelsList = self.baseWorkflow?.getNodesList().map((node) => node.getModel()) || [];

      // Function to fetch all model types
      const fetchAllModelTypes = async (perPage = 128) => {
        const allModelTypes = [];
        let page = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await grpc.listModelTypes({
            userOrOrgId,
            appId,
            page,
            perPage,
          });

          const modelTypes = pipe(
            response,
            either.fold(
              (error) => {
                console.error(error);
                return [];
              },
              (data) => data.getModelTypesList(),
            ),
          );

          allModelTypes.push(...modelTypes);
          hasMorePages = modelTypes.length === perPage;
          page += 1;
        }

        return allModelTypes;
      };

      // Function to find rankable model
      const findRankableModel = (_modelsList: Model[], modelTypes: ModelType[]) => {
        for (const modelType of modelTypes) {
          for (const model of _modelsList) {
            if (modelType.getId() === model?.getModelTypeId() && modelType.getOutputFieldsList().some((field) => field.includes('embed'))) {
              return model;
            }
          }
        }
        return null;
      };

      // Function to fetch all concepts
      const fetchAllConceptsList = async (rankableModel?: Model, perPage = 500) => {
        const conceptsList = [];
        let page = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await grpc.postConceptsSearches({
            userOrOrgId,
            appId,
            page,
            perPage,
            name: '',
            includeMainAppConcepts: false,
            outsideApp: false,
            fuzzy: false,
            rankableModel,
          });

          const concepts = pipe(
            response,
            either.fold(
              (error) => {
                console.error(error);
                return [];
              },
              (data) => data.getConceptsList(),
            ),
          );

          conceptsList.push(...concepts);
          hasMorePages = concepts.length === perPage;
          page += 1;
        }

        return conceptsList;
      };

      // Fetch model types and rankable model
      const modelTypes = await fetchAllModelTypes();
      if (!modelTypes) {
        console.error('Failed to fetch model types');
      }

      const _rankableModel = findRankableModel(modelsList as Model[], modelTypes);
      self.setRankableModel(_rankableModel);
      const conceptsList = await fetchAllConceptsList(_rankableModel || undefined);

      // Separate trained and untrained concepts
      const [trainedConcepts, unTrainedConcepts] = conceptsList.reduce<[Concept[], Concept[]]>(
        ([trained, untrained], concept) => {
          const extraInfo = concept.getExtraInfo();
          const isRankable = extraInfo?.getIsRankable() || false;

          return isRankable ? [[...trained, concept], untrained] : [trained, [...untrained, concept]];
        },
        [[], []],
      );
      updateMST(self, () => {
        self.trainedConcepts.replace(trainedConcepts);
        self.untrainedConcepts.replace(unTrainedConcepts);
        self.allConceptsFetched = true;
      });
      return null;
    },

    conceptIsTrained(id: string) {
      return self.trainedConcepts.find((concept) => concept.getId() === id) !== undefined;
    },

    /**
     * Use text to search for a concept. Since we already loaded all concepts we just filter everything locally.
     */
    searchConcepts(filterText: string) {
      const validConceptName = createValidConceptName(filterText);
      return Promise.resolve(self.allConcepts.filter((concept) => concept.getName().includes(validConceptName)));
    },
    getConceptById(id: string, concepts: typeof self.allConcepts = self.allConcepts) {
      return concepts.find((concept) => concept.getId() === id);
    },
    getConceptByName(name: string, concepts: typeof self.allConcepts = self.allConcepts) {
      return concepts.find((concept) => concept.getName().toLowerCase() === name.toLowerCase());
    },
  }))
  .actions((self) => ({
    async afterCreate() {
      await self.fetchAllConcepts();
    },
  }))
  .actions((self) => ({
    getConceptNameById(id: string) {
      const concept = self.getConceptById(id);
      // id is a fallback in case concept is not found
      return concept ? concept.getName() : id;
    },
    /**
     * Create a new concept with the given name
     */
    createConcept: async (newConceptId: string, newConceptName: string) => {
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      await createConcept({
        request: {
          userOrOrgId,
          appId,
        },
        concept: {
          name: newConceptName,
          id: newConceptId,
          app_id: appId,
        },
      });

      await self.fetchAllConcepts();
      const newConcept = self.allConcepts.find((concept) => concept.getId() === newConceptId);

      // difficult to test, need to change listConcepts request mid test
      // istanbul ignore next
      if (newConcept) {
        self.addConceptFilter({
          id: newConcept.getId(),
          negate: false,
        });
        return newConcept;
      }
      return null;
    },
  }));
